<template>
  <b-modal
    ref="upsertModal"
    v-model="showModal"
    centered
    size="md"
    :no-close-on-backdrop="true"
    cancel-variant="danger"
    title="Change Status"
    @hidden="clearForm"
  >
    <b-overlay
      variant="transparent"
      opacity="0.6"
    >
      <ValidationObserver ref="vObserver">
        <b-row>
          <b-col cols="12 my-2">
            <label class="h6 mb-1"> Select Status: </label>
            <ValidationProvider
              v-slot="{ errors }"
              name="Status"
              rules="required"
            >
              <b-form-group>
                <v-select
                  v-model="currentStatus"
                  label="name"
                  :options="options"
                  :reduce="option => option.status"
                  class="select-size-lg"
                  placeholder="Select Status"
                />
              </b-form-group>
              <span class="validation__red">{{ errors[0] }}</span>
            </ValidationProvider>
          </b-col>
        </b-row>
      </ValidationObserver>
    </b-overlay>

    <template #modal-footer>
      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
        class="d-inline-block"
      >
        <b-button
          variant="primary"
          @click="submit"
        >
          Change
        </b-button>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  computed, onMounted, ref, watch,
} from '@vue/composition-api'
import {
  BModal,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BOverlay,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import useOrder from '@/composables/useOrder'
import useLoading from '@/composables/useLoading'
import api from '@/services/api'
import router from '@/router'
import { isObject } from '@core/utils/utils'

export default {
  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BFormGroup,
    BOverlay,
    vSelect,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    state: {
      type: Number,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:show', 'update:data'],
  setup(props, { emit }) {
    const {
      isLoading,
      startLoading,
      finishLoading,
    } = useLoading()
    const vObserver = ref(null)
    const options = ref([])
    const currentStatus = ref(null)
    const { statuses } = useOrder()

    const showModal = computed({
      get() {
        return props.show
      },
      set(v) {
        emit('update:show', v)
      },
    })

    function clearForm() {
      currentStatus.value = null
    }

    watch(showModal, vShow => {
      if (vShow) {
        currentStatus.value = options.value.find(({ status }) => status === props.state)
      }
    })

    async function submit() {
      const isValid = await vObserver.value.validate()

      if (!isValid) {
        return
      }

      try {
        startLoading()

        await api.order.changeStatus({
          id: router.currentRoute.params.id,
          body: {
            status: isObject(currentStatus.value) ? currentStatus.value.status : currentStatus.value,
          },
        })
        showModal.value = false
        emit('update:data')
        clearForm()
        vObserver.value.reset()
      } finally {
        finishLoading()
      }
    }

    function setOptionsValue() {
      Object.keys(statuses).forEach(status => {
        options.value.push({
          // eslint-disable-next-line radix
          status: parseInt(status),
          name: statuses[status].value,
        })
      })
    }

    onMounted(() => {
      setOptionsValue()
    })

    return {
      options,
      currentStatus,

      vObserver,
      isLoading,
      showModal,
      clearForm,
      submit,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
